"use client"
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import Logo from '@/components/Logo'

type Category = {
  slug: string
  name_fr: string
  name_es: string
  show_in_nav: boolean
  order: number
}

interface FooterProps {
  categories?: Category[]
}

export default function Footer({ categories = [] }: FooterProps) {
  const t = useTranslations('nav')
  const tf = useTranslations('footer')
  const tui = useTranslations('ui')
  const locale = useLocale()

  return (
    <footer className="bg-gray-900 text-white">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Main footer content */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-6">
          {/* Logo and description */}
          <div className="lg:col-span-2">
            <Logo variant="footer" className="text-white" />
            <p className="mt-4 text-sm leading-relaxed text-gray-300">
              {tf('description')}
            </p>
            {/* Bilingual address */}
            <div className="mt-4 text-sm text-gray-300 space-y-1" itemScope itemType="https://schema.org/Organization">
              <meta itemProp="name" content="Msintapujo" />
              <p className="font-semibold text-white">
                {locale === 'es' ? 'EL SITIO DE LOS HISPANOS EN CANADÁ' : 'LE SITE DES HISPANOS AU CANADA'}
              </p>
              <p>{locale === 'es' ? 'UNA DIVISIÓN DE LATIN MEDIA INC' : 'UNE DIVISION DE LATIN MEDIA INC'}</p>
              <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress" className="space-y-1">
                <p itemProp="streetAddress">BOULEVARD MÉTROPOLITAIN E.</p>
                <p>
                  <span itemProp="addressLocality">{locale === 'es' ? 'Montreal' : 'Montréal'}</span>,{' '}
                  <span itemProp="addressRegion">Québec</span>,{' '}
                  <span itemProp="postalCode">H1R 1Z6</span>
                </p>
                <p itemProp="addressCountry">{locale === 'es' ? 'Canadá' : 'Canada'}</p>
              </div>
              <p>
                {locale === 'es' ? 'TEL.:' : 'TÉL. :'}{' '}
                <a itemProp="telephone" href="tel:+15143602941" className="underline hover:text-white">(514) 360-2941</a>
              </p>
            </div>
            <div className="mt-6 flex space-x-4">
              <a 
                href="https://www.facebook.com/MSinTapujos" 
                className="rounded-full bg-blue-600 p-2 text-white transition-colors hover:bg-blue-700"
                aria-label="Facebook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a 
                href="https://x.com/msintapujos" 
                className="rounded-full bg-sky-500 p-2 text-white transition-colors hover:bg-sky-600"
                aria-label="X (Twitter) - @msintapujos"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </a>
              <a 
                href="https://www.youtube.com/@marcanosintapujos" 
                className="rounded-full bg-red-600 p-2 text-white transition-colors hover:bg-red-700"
                aria-label="YouTube - @marcanosintapujos"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
              <a 
                href="https://www.linkedin.com/in/marcano-sin-tapujos-26835895" 
                className="rounded-full bg-blue-700 p-2 text-white transition-colors hover:bg-blue-800"
                aria-label="LinkedIn - Marcano Sin Tapujos"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a 
                href="https://www.instagram.com/msintapujos/?hl=es" 
                className="rounded-full bg-pink-600 p-2 text-white transition-colors hover:bg-pink-700"
                aria-label="Instagram - @msintapujos"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.405a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400">
              {tf('navigation')}
            </h3>
            <ul className="mt-6 space-y-4">
              <li>
                <Link href={`/${locale}`} className="text-base text-gray-300 transition-colors hover:text-white">
                  {t('home')}
                </Link>
              </li>
              <li>
                <Link href={`/${locale}/about`} className="text-base text-gray-300 transition-colors hover:text-white">
                  {t('about')}
                </Link>
              </li>
              <li>
                <Link href={`/${locale}/contact`} className="text-base text-gray-300 transition-colors hover:text-white">
                  {t('contact')}
                </Link>
              </li>
              <li>
                <Link href={`/${locale}/videos`} className="text-base text-gray-300 transition-colors hover:text-white">
                  {t('videos')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Categories - Dynamiques comme la navbar */}
          {categories.length > 0 && (
            <>
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400">
                  {tui('categories')}
                </h3>
                <ul className="mt-6 space-y-4">
                  {categories.slice(0, 4).map((category) => {
                    const categoryName = locale === 'fr' ? category.name_fr : category.name_es
                    return (
                      <li key={category.slug}>
                        <Link 
                          href={`/${locale}/${category.slug}`} 
                          className="text-base text-gray-300 transition-colors hover:text-white"
                        >
                          {categoryName}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </div>

              {/* More Categories - Catégories restantes */}
              {categories.length > 4 && (
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400">
                    {tf('moreCategories')}
                  </h3>
                  <ul className="mt-6 space-y-4">
                    {categories.slice(4).map((category) => {
                      const categoryName = locale === 'fr' ? category.name_fr : category.name_es
                      return (
                        <li key={category.slug}>
                          <Link 
                            href={`/${locale}/${category.slug}`} 
                            className="text-base text-gray-300 transition-colors hover:text-white"
                          >
                            {categoryName}
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* Newsletter removed as requested */}
        </div>

        {/* Bottom section */}
        <div className="mt-16 border-t border-gray-800 pt-8">
          <div className="flex flex-col items-center justify-between lg:flex-row">
            <div className="flex flex-col items-center space-y-2 lg:flex-row lg:space-y-0 lg:space-x-8">
              <p className="text-sm text-gray-400">
                &copy; {new Date().getFullYear()} Marcano Sin Tapujos. {tf('copyright')}.
              </p>
              <div className="flex space-x-6">
                <a href={`/${locale}/politique-confidentialite`} className="text-sm text-gray-400 transition-colors hover:text-white">
                  {tf('privacyPolicy')}
                </a>
                <a href={`/${locale}/conditions-utilisation`} className="text-sm text-gray-400 transition-colors hover:text-white">
                  {tf('termsOfUse')}
                </a>
                <a href={`/${locale}/mentions-legales`} className="text-sm text-gray-400 transition-colors hover:text-white">
                  {tf('legalMentions')}
                </a>
              </div>
            </div>
            <div className="mt-4 flex items-center space-x-2 lg:mt-0">
              <span className="text-sm text-gray-400">{tf('madeWithLove')}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

